import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import './css/UserDetails.css';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: ''
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      setIsLoading(true);
      try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          setUserDetails(userDoc.data());
        } else {
          console.log("No such document!");
          alert("User not found!");
          navigate("/manage-users");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        alert("Failed to load user details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId, navigate]);

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    document.body.style.overflow = 'hidden';
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const openEmailModal = () => {
    setShowEmailModal(true);
    setEmailData({
      subject: `Message from Admin - ${userDetails.name || 'User'}`,
      message: ''
    });
    document.body.style.overflow = 'hidden';
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setEmailData({ subject: '', message: '' });
    document.body.style.overflow = 'auto';
  };

  const handleSendEmail = () => {
    if (!emailData.message.trim()) {
      alert('Please enter a message');
      return;
    }

    // Create mailto link
    const mailtoLink = `mailto:${userDetails.email}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.message)}`;
    
    // Open default email client
    window.location.href = mailtoLink;
    
    // Close modal after a short delay
    setTimeout(() => {
      closeEmailModal();
    }, 500);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <h3>Loading user details...</h3>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="error-container">
        <i className="fas fa-exclamation-circle"></i>
        <h3>User not found</h3>
        <button onClick={() => navigate("/manage-users")} className="back-btn">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="user-details-wrapper">
      {/* Header Section */}
      <div className="details-header">
        <div className="header-left">
          <button onClick={() => navigate("/manage-users")} className="back-btn">
            <i className="fas fa-arrow-left"></i> Back
          </button>
          <h2><i className="fas fa-user-circle"></i> User Profile Details</h2>
        </div>
        <div className="header-right">
          <button 
            onClick={openEmailModal} 
            className="send-email-btn"
          >
            <i className="fas fa-envelope"></i> Send Email
          </button>
          <button 
            onClick={() => navigate(`/edit-user/${userId}`)} 
            className="edit-btn-header"
          >
            <i className="fas fa-edit"></i> Edit Profile
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="details-content">
        {/* Personal Information Section */}
        <div className="details-section">
          <h3 className="section-title">
            <i className="fas fa-user"></i> Personal Information
          </h3>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-user"></i> Name
              </span>
              <span className="detail-value">{userDetails.name || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-envelope"></i> Email
              </span>
              <span className="detail-value">{userDetails.email || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-map-marker-alt"></i> Location
              </span>
              <span className="detail-value">{userDetails.location || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-birthday-cake"></i> Date of Birth
              </span>
              <span className="detail-value">{userDetails.dateOfBirth || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-calendar-check"></i> Date of Joining
              </span>
              <span className="detail-value">
                {userDetails.createdAt?.toDate().toLocaleDateString("en-IN", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                }) || "N/A"}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-phone"></i> Phone Number
              </span>
              <span className="detail-value">{userDetails.phoneNumber || "N/A"}</span>
            </div>
          </div>
        </div>

        {/* Gender & Preferences Section */}
        <div className="details-section">
          <h3 className="section-title">
            <i className="fas fa-venus-mars"></i> Gender & Preferences
          </h3>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-venus-mars"></i> Gender
              </span>
              <span className="detail-value">{userDetails.userGender || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-heart"></i> Gender Preference
              </span>
              <span className="detail-value">{userDetails.genderPreference || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-search-heart"></i> Looking For
              </span>
              <span className="detail-value">{userDetails.lookingFor || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-ruler-vertical"></i> Height
              </span>
              <span className="detail-value">{userDetails.height || "N/A"}</span>
            </div>
          </div>
        </div>

        {/* Cultural Background Section */}
        <div className="details-section">
          <h3 className="section-title">
            <i className="fas fa-globe"></i> Cultural Background
          </h3>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-praying-hands"></i> Religion
              </span>
              <span className="detail-value">{userDetails.religion || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-language"></i> Mother Tongue
              </span>
              <span className="detail-value">{userDetails.motherTongue || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-users"></i> Community
              </span>
              <span className="detail-value">{userDetails.community || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-flag"></i> Grew Up In
              </span>
              <span className="detail-value">{userDetails.growUpCountry || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-home"></i> Currently Living In
              </span>
              <span className="detail-value">{userDetails.settledCountry || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-map-marked"></i> Origin
              </span>
              <span className="detail-value">{userDetails.originCountry || "N/A"}</span>
            </div>
          </div>
        </div>

        {/* Education & Career Section */}
        <div className="details-section">
          <h3 className="section-title">
            <i className="fas fa-graduation-cap"></i> Education & Career
          </h3>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-certificate"></i> Degree
              </span>
              <span className="detail-value">{userDetails.degree || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-briefcase"></i> Profession
              </span>
              <span className="detail-value">{userDetails.dayJob || "N/A"}</span>
            </div>
          </div>
        </div>

        {/* Subscription Details Section */}
        <div className="details-section">
          <h3 className="section-title">
            <i className="fas fa-crown"></i> Subscription Details
          </h3>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-star"></i> Plan
              </span>
              <span className={`detail-value plan-badge ${userDetails.subscriptions?.planName?.toLowerCase().replace(/\s+/g, '-')}`}>
                {userDetails.subscriptions?.planName || "Free"}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-calendar-alt"></i> Valid Until
              </span>
              <span className="detail-value">{userDetails.subscriptions?.validUntil || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-tags"></i> Discount
              </span>
              <span className="detail-value discount-badge">
                {userDetails.currentDiscountPercentage || 0}%
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                <i className="fas fa-eye"></i> Profile Discovery
              </span>
              <span className={`detail-value status-badge ${userDetails.profileDiscovery ? 'active' : 'inactive'}`}>
                {userDetails.profileDiscovery ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        {/* Referral Information Section */}
        {(userDetails.referrerUserId || userDetails.referrerUserName) && (
          <div className="details-section">
            <h3 className="section-title">
              <i className="fas fa-user-friends"></i> Referral Information
            </h3>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">
                  <i className="fas fa-id-badge"></i> Referrer User ID
                </span>
                <span className="detail-value">{userDetails.referrerUserId || "N/A"}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <i className="fas fa-user"></i> Referrer Name
                </span>
                <span className="detail-value">{userDetails.referrerUserName || "N/A"}</span>
              </div>
            </div>
          </div>
        )}

        {/* About Me Section */}
        {userDetails.aboutMe && (
          <div className="details-section full-width">
            <h3 className="section-title">
              <i className="fas fa-comment-dots"></i> About Me
            </h3>
            <div className="about-me-content">
              <p>{userDetails.aboutMe}</p>
            </div>
          </div>
        )}

        {/* Profile Images Section */}
        <div className="details-section full-width">
          <h3 className="section-title">
            <i className="fas fa-images"></i> Profile Images
          </h3>
          <div className="images-grid">
            {userDetails.profileImageUrls && userDetails.profileImageUrls.length > 0 ? (
              userDetails.profileImageUrls.map((url, index) => (
                <div key={index} className="image-card-view">
                  <div className="image-wrapper-view" onClick={() => openImageModal(url)}>
                    <img 
                      src={url} 
                      alt={`Profile ${index + 1}`}
                      loading="lazy"
                    />
                    <div className="image-overlay-view">
                      <i className="fas fa-search-plus"></i>
                    </div>
                  </div>
                  <span className="image-number">Image {index + 1}</span>
                </div>
              ))
            ) : (
              <div className="no-images">
                <i className="fas fa-image"></i>
                <p>No profile images available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal-overlay-view" onClick={closeImageModal}>
          <div className="image-modal-container-view">
            <button className="modal-close-btn-view" onClick={closeImageModal}>
              <i className="fas fa-times"></i>
            </button>
            <div className="modal-image-wrapper-view">
              <img src={selectedImage} alt="Full size profile" />
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="email-modal-overlay" onClick={closeEmailModal}>
          <div className="email-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="email-modal-header">
              <h3>
                <i className="fas fa-envelope"></i> Send Email to {userDetails.name}
              </h3>
              <button className="email-modal-close" onClick={closeEmailModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="email-modal-body">
              <div className="email-field">
                <label>
                  <i className="fas fa-user"></i> To:
                </label>
                <input 
                  type="text" 
                  value={userDetails.email} 
                  disabled 
                  className="email-input-disabled"
                />
              </div>

              <div className="email-field">
                <label>
                  <i className="fas fa-heading"></i> Subject:
                </label>
                <input 
                  type="text" 
                  value={emailData.subject}
                  onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                  placeholder="Enter email subject"
                  className="email-input"
                />
              </div>

              <div className="email-field">
                <label>
                  <i className="fas fa-comment"></i> Message:
                </label>
                <textarea 
                  value={emailData.message}
                  onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                  placeholder="Enter your message here..."
                  className="email-textarea"
                  rows="8"
                />
              </div>
            </div>

            <div className="email-modal-footer">
              <button className="email-cancel-btn" onClick={closeEmailModal}>
                <i className="fas fa-times"></i> Cancel
              </button>
              <button className="email-send-btn" onClick={handleSendEmail}>
                <i className="fas fa-paper-plane"></i> Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;